import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { companyService } from '../services/api';
import './Companies.css';

function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('All');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const data = await companyService.getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
      alert('Error loading companies. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getLocation = (company) => {
    const parts = [];
    if (company.Location) parts.push(company.Location);
    if (company.District?.Name) parts.push(company.District.Name);
    if (company.State?.Name) parts.push(company.State.Name);
    if (company.Country?.Name) parts.push(company.Country.Name);
    return parts.length > 0 ? parts.join(', ') : 'Location not specified';
  };

  const handleViewProfile = (company) => {
    setSelectedCompany(company);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         getLocation(company).toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.Code?.toLowerCase().includes(searchTerm.toLowerCase());
    // For now, we'll keep the filter but it won't filter by industry since API doesn't provide it
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="companies">
        <div className="page-header">
          <div className="container">
            <h1>Companies</h1>
            <p>Discover top companies hiring through our platform</p>
          </div>
        </div>
        <div className="container" style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Loading companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="companies">
      <div className="page-header">
        <div className="container">
          <h1>Companies</h1>
          <p>Discover top companies hiring through our platform</p>
        </div>
      </div>

      <div className="container">
        <div className="search-filter-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search companies by name, code or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredCompanies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>No companies found.</p>
          </div>
        ) : (
          <div className="companies-grid">
            {filteredCompanies.map(company => (
              <div key={company.Id} className="company-card">
                <div className="company-header">
                  <h3>{company.Name}</h3>
                  {company.Code && <span className="open-positions">Code: {company.Code}</span>}
                </div>
                <div className="company-content">
                  <p className="company-location">📍 {getLocation(company)}</p>
                  {company.Email && <p className="company-industry">📧 {company.Email}</p>}
                  {company.MobileNo && <p className="company-employees">📞 {company.MobileNo}</p>}
                  {company.Address && (
                    <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                      📍 {company.Address}
                    </p>
                  )}
                  {company.Description && (
                    <p className="company-description">{company.Description}</p>
                  )}
                  {company.Logo && (
                    <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                      <img 
                        src={company.Logo.includes('http') ? company.Logo : `http://localhost:59844/${company.Logo}`} 
                        alt={company.Name}
                        style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }}
                      />
                    </div>
                  )}
                  <div className="company-actions">
                    <Link to={`/company/${company.Id}/jobs`} className="view-jobs-btn-link">
                      <button className="view-jobs-btn">View Jobs</button>
                    </Link>
                    <button className="view-profile-btn" onClick={() => handleViewProfile(company)}>View Profile</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Company Profile Modal */}
      {isModalOpen && selectedCompany && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedCompany.Name}</h2>
              <button className="modal-close-btn" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {selectedCompany.Logo && (
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <img 
                    src={selectedCompany.Logo.includes('http') ? selectedCompany.Logo : `http://localhost:59844/${selectedCompany.Logo}`} 
                    alt={selectedCompany.Name}
                    style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain' }}
                  />
                </div>
              )}
              <div className="modal-info-section">
                {selectedCompany.Code && (
                  <p><strong>Code:</strong> {selectedCompany.Code}</p>
                )}
                <p><strong>Location:</strong> {getLocation(selectedCompany)}</p>
                {selectedCompany.Address && (
                  <p><strong>Address:</strong> {selectedCompany.Address}</p>
                )}
                {selectedCompany.Email && (
                  <p><strong>Email:</strong> {selectedCompany.Email}</p>
                )}
                {selectedCompany.MobileNo && (
                  <p><strong>Mobile:</strong> {selectedCompany.MobileNo}</p>
                )}
                {selectedCompany.Description && (
                  <div style={{ marginTop: '1rem' }}>
                    <strong>Description:</strong>
                    <p style={{ marginTop: '0.5rem', lineHeight: '1.6' }}>{selectedCompany.Description}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <Link to={`/company/${selectedCompany.Id}/jobs`} className="view-jobs-btn-link">
                <button className="view-jobs-btn">View Jobs</button>
              </Link>
              <button className="modal-close-button" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Companies;

